import * as React from 'react';
import { AlertCircle, RefreshCw, Inbox, Lock } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './button';
import { useI18n } from '@/lib/i18n';

// UI_BUILD_SPEC §2.21. Three distinct states that pages kept reimplementing as
// a bare centred <p>: empty, filtered-empty, and error. Copy rules from §7 —
// name the next action, never the absence.

interface EmptyStateProps {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}

export function EmptyState({ icon: Icon = Inbox, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-12 text-center', className)}>
      <Icon className="h-10 w-10 text-ink-subtle" aria-hidden="true" />
      <h3 className="mt-4 text-lg font-semibold text-ink">{title}</h3>
      {description && <p className="mt-1 max-w-[60ch] text-base text-ink-muted">{description}</p>}
      {action && <div className="mt-5 flex flex-wrap justify-center gap-3">{action}</div>}
    </div>
  );
}

/**
 * Filtered-empty is a different state from empty (§2.21) — the data exists, the
 * filters hide it, so the escape hatch is clearing them rather than creating.
 */
export function FilteredEmptyState({ onClear, className }: { onClear: () => void; className?: string }) {
  const { t } = useI18n();
  return (
    <EmptyState
      title={t('ui.filteredEmptyTitle')}
      description={t('ui.filteredEmptyDescription')}
      action={<Button variant="outline" size="sm" onClick={onClear}>{t('ui.clearFilters')}</Button>}
      className={className}
    />
  );
}

interface ErrorStateProps {
  title?: string;
  message?: string;
  correlationId?: string;
  onRetry?: () => void;
  className?: string;
}

/**
 * Inline error block (§2.21). Never used for field validation — that lives on
 * the control itself (§2.3) — and never renders a raw status code (§7).
 */
export function ErrorState({
  title,
  message,
  correlationId,
  onRetry,
  className,
}: ErrorStateProps) {
  const { t } = useI18n();
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-3 rounded-md border border-danger-500/20 border-l-4 border-l-danger-500 bg-danger-50 p-4',
        className,
      )}
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger-500" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-base font-medium text-ink">{title ?? t('ui.errorTitle')}</p>
        <p className="mt-0.5 text-sm text-ink-muted">{message ?? t('ui.errorMessage')}</p>
        {correlationId && (
          <p className="mt-1 font-mono text-xs text-ink-subtle">{t('ui.reference', { id: correlationId })}</p>
        )}
      </div>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw aria-hidden="true" />
          {t('ui.retry')}
        </Button>
      )}
    </div>
  );
}

/** §7 permission copy: explain and route, never a bare "Access denied". */
export function NoPermissionState({ area, className }: { area?: string; className?: string }) {
  const { t } = useI18n();
  return (
    <EmptyState
      icon={Lock}
      title={t('ui.noAccess', { area: area ?? t('ui.thisArea') })}
      description={t('ui.noAccessHint')}
      className={className}
    />
  );
}
